import React, {useState, useRef, useEffect, useCallback} from 'react';
import {BellIcon} from '../../icons/bell';
import styles from './NotificationPopup.module.scss';
import Typography from 'components/Typography';
import {useNotification} from './useNotification';
import {NotificationType} from 'types/notification-settings';
import {UserIcon} from 'pages/DeliveryPlanner/components/Details/icons';
import {
  RouteIcon,
  BoxIcon,
  BankNoteArrowDownIcon,
  CheckIcon,
} from 'components/TopBar/icons';
import FilterIcon from 'pages/Requests/FilterIcon';
import {format, parseISO} from 'date-fns';
import Spinner from 'components/Spinner';
import {CompanyNotificationLog} from 'types/notification';
import {useHistory} from 'react-router-dom';

const notificationTypeIconMap: Record<NotificationType, React.ReactNode> = {
  new_customer: <UserIcon width={18} height={18} />,
  new_order: <BoxIcon width={18} height={18} />,
  new_route: <RouteIcon width={18} height={18} />,
  customer_payment: <BankNoteArrowDownIcon width={30} height={30} />,
};

const notificationTypeLabels: Record<NotificationType, string> = {
  customer_payment: 'Customer Payment',
  new_customer: 'New Customer',
  new_order: 'New Order',
  new_route: 'New Route',
};

const ALL_NOTIFICATION_TYPES: NotificationType[] = [
  'customer_payment',
  'new_customer',
  'new_order',
  'new_route',
];

const NotificationPopup: React.FC = () => {
  const {
    notifications,
    isLoading,
    unreadCount,
    notificationTypes,
    setNotificationTypes,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    onMarkAsRead,
    onMarkAllAsRead,
  } = useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const {push} = useHistory();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFilterOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (isOpen) setIsFilterOpen(false);
  };

  const handleAction = (notification: CompanyNotificationLog) => {
    const notificationType = notification.notification_type;
    const notificationPayload = notification.payload;
    if (notificationType === 'new_order') {
      push(`/orders/${notificationPayload.sequential_order_number}`);
    } else if (notificationType === 'new_route') {
      push(`/history/routes/${notificationPayload.id}`);
    } else if (notificationType === 'new_customer') {
      push(`/people/customers/${notificationPayload.id}/edit`);
    } else if (notificationType === 'customer_payment') {
      push(`/transactions/${notificationPayload.transaction_id}`);
    }
    onMarkAsRead({id: notification.id});
    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    await onMarkAllAsRead();
    setIsOpen(false);
  };

  const toggleFilterType = (type: NotificationType) => {
    const updated = notificationTypes.includes(type)
      ? notificationTypes.filter((t) => t !== type)
      : [...notificationTypes, type];
    setNotificationTypes(updated);
  };

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    if (nearBottom) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className={styles.container} ref={menuRef}>
      <div className={styles.notificationIcon} onClick={handleToggle}>
        <BellIcon width={22} height={22} />
        {unreadCount > 0 && (
          <div className={styles.notificationBadge}>{unreadCount}</div>
        )}
      </div>

      {isOpen && (
        <div className={styles.popup}>
          <div className={styles.popupHeader}>
            <Typography variant="p">Notifications</Typography>
            <div className={styles.headerActions}>
              <div
                className={styles.filterIcon}
                onClick={() => setIsFilterOpen((prev) => !prev)}
              >
                <FilterIcon
                  width={20}
                  height={20}
                  isFilled={notificationTypes.length > 0}
                />
              </div>
              <div className={styles.checkIcon} onClick={handleMarkAllAsRead}>
                <CheckIcon width={24} height={24} />
              </div>
            </div>
          </div>

          {isFilterOpen && (
            <div className={styles.filterDropdown}>
              {ALL_NOTIFICATION_TYPES.map((type) => (
                <label key={type} className={styles.filterItem}>
                  <input
                    type="checkbox"
                    checked={notificationTypes.includes(type)}
                    onChange={() => toggleFilterType(type)}
                  />
                  <span>{notificationTypeLabels[type]}</span>
                </label>
              ))}
            </div>
          )}

          {isLoading ? (
            <Spinner className={styles.loadingSpinner} />
          ) : (
            <div
              className={styles.popupContent}
              ref={contentRef}
              onScroll={handleScroll}
            >
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={styles.item}
                  onClick={() => handleAction(notification)}
                >
                  {notificationTypeIconMap[notification.notification_type]}
                  <div>
                    <Typography variant="p">{notification.title}</Typography>
                    <Typography
                      variant="label"
                      className={styles.notificationDate}
                    >
                      {format(
                        parseISO(notification.created_at),
                        'MMM d, yyyy hh:mm a'
                      )}
                    </Typography>
                  </div>
                  {!notification.read_at && (
                    <div className={styles.unreadDot} />
                  )}
                </div>
              ))}
              {isFetchingNextPage && (
                <Spinner className={styles.loadingSpinner} />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationPopup;
